import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Flame } from 'lucide-react';
import HallOfShameModal from './HallOfShameModal';

const EMOJI_CHAR = {
  thumbs_up: '👍', thumbs_down: '👎', heart: '❤️',
  fire: '🔥', eggplant: '🍆', splash: '💦',
};

const proxyUrl = (url) => {
  if (!url) return '';
  if (url.includes('mecklenburgcountync.gov')) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/mugshots/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const firstName = (name) =>
  name?.includes(',') ? name.split(',')[1].trim().split(' ')[0] : (name || '').split(' ')[0];

const MostReactedGallery = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mugshots/most-reacted?limit=10`)
      .then((r) => r.json())
      .then((d) => { if (active && d.success) setRecords(d.records || []); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading || records.length === 0) return null;

  return (
    <div className="container mx-auto max-w-6xl mt-8 md:mt-12 px-2 sm:px-0" data-testid="most-reacted-gallery">
      <div className="flex items-center gap-2 mb-3 sm:mb-4 px-1">
        <Flame className="w-5 h-5 text-orange-400" />
        <h3 className="text-lg sm:text-xl font-heading font-bold">
          10 Most <span className="text-gradient-primary">Reacted</span> Mugshots
        </h3>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-3 snap-x sm:grid sm:grid-cols-4 sm:overflow-visible">
        {records.map((rec, i) => {
          const topEmojis = Object.entries(rec.emoji_counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3);
          return (
            <motion.div
              key={rec.record_id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="snap-start shrink-0 w-36 sm:w-auto"
              data-testid={`reacted-card-${rec.record_id}`}
            >
              <Card
                onClick={() => setSelected(rec)}
                className="overflow-hidden bg-gradient-card border-border hover:border-primary/40 transition-colors cursor-pointer group"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={proxyUrl(rec.mugshot_url)}
                    alt={rec.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold text-white px-2 py-1 rounded-full bg-orange-600/90">
                      Share 🔥
                    </span>
                  </div>
                  <div className="absolute top-1.5 right-1.5 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/65 backdrop-blur-sm text-white text-xs font-semibold">
                    <Flame className="w-3 h-3 text-orange-400" />
                    {rec.total_reactions}
                  </div>
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold truncate">{firstName(rec.name)}</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {topEmojis.map(([emoji, count]) => (
                      <span key={emoji} className="flex items-center gap-0.5 text-xs text-muted-foreground">
                        <span className="text-sm leading-none">{EMOJI_CHAR[emoji] || ''}</span>
                        {count}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <HallOfShameModal record={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default MostReactedGallery;
